import clsx from "clsx";
import React from "react";
import type { DetailData } from "../../../../../contexts/chat-log/chat-log-context";
import styles from "./styles.module.scss";
import { useGSAP } from "@gsap/react";
import { lineAnimation } from "./fns";

// Skeleton loading component
const Skeleton = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "skeleton-loading 1.5s ease-in-out infinite",
    }}
  />
);

export default function LinkCard({
  className,
  file_path,
  file_name,
  thumbnail,
  link,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"div">, "children"> &
  Pick<DetailData, "file_path" | "file_name" | "thumbnail" | "link">) {
  const container = React.useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useGSAP(
    () => {
      lineAnimation();
    },
    { scope: container }
  );

  React.useEffect(() => {
    // Simulate 3 seconds loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    const url = link || file_path;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // URLからドメイン名を抽出
  const getDomainName = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return (
    <div
      className={clsx(styles.wrap, styles.clickable, className)}
      {...props}
      ref={container}
      onClick={handleClick}
      style={{
        cursor: "pointer",
        width: "200px",
        height: "200px",
        minWidth: "200px",
        maxWidth: "200px",
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        border: "1px solid #d0d0d0",
        borderRadius: "0",
        backgroundColor: "#fff",
        isolation: "isolate",
        willChange: "auto",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        contain: "layout style paint"
      }}
    >
      <svg
        className={styles.svg}
        width="200"
        height="200"
        viewBox="0, 0, 200, 200"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
      >
        <rect
          className={clsx(styles.rect, "js-rect")}
          x="2"
          y="2"
          width="196"
          height="196"
          fill="transparent"
        />
      </svg>
      <div
        className={styles.content}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          borderRadius: "0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px"
        }}
      >
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={file_name || "Link"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "0"
                }}
              />
            ) : (
              <>
                {/* リンクアイコン */}
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1976d2"
                  strokeWidth="2"
                  style={{ marginBottom: "15px" }}
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <div style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#333",
                  textAlign: "center",
                  wordBreak: "break-word",
                  marginBottom: "8px"
                }}>
                  {file_name || "参考リンク"}
                </div>
                <div style={{
                  fontSize: "11px",
                  color: "#999",
                  textAlign: "center",
                  wordBreak: "break-all"
                }}>
                  {getDomainName(file_path)}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
